import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AuthFieldContainer,
  AuthFieldLabel,
  AuthFormContainer,
  AuthFormHint,
  AuthInputField,
  AuthSubmitButton,
} from '../LoginPagesUI';
import Cognito from '../../utils/Cognito';
import { AxiosError } from 'axios';
import { ICognitoResponse, ILoginInput, LoginStep } from '../types';
import { TOAST_ERROR_MESSAGE } from '../../utils/utils';
import FieldError from '../../../../components/Form/FieldError';
import { Updater } from 'use-immer';
import { goBackToLoginStep } from './utils';
import toastMessage from '../../../../utils/toastMessage';

interface IProps {
  email: string;
  setLoginStep: (step: LoginStep) => void;
  setLoginInput: Updater<ILoginInput>;
}

const MfaSetupForm = ({ email, setLoginInput, setLoginStep }: IProps) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [verifyToken, setVerifyToken] = useState('');
  const [hasError, setHasError] = useState(false);

  const onVerifyMfa = (e: React.MouseEvent) => {
    e.preventDefault();
    setLoading(true);
    Cognito.verifyMfa(email, verifyToken)
      .then(() => {
        navigate('/');
      })
      .catch((error: AxiosError<ICognitoResponse>) => {
        setLoading(false);
        const status = error.response?.data.status;
        console.error('Error on verify token', error.response?.data);
        switch (status) {
          case 400:
            setHasError(true);
            return;
          case 401:
            goBackToLoginStep({ setLoginStep, setLoginInput });
            toastMessage({
              message:
                'Your session has expired. Redirecting to the login page.',
              type: 'info',
            });
            return;
          default:
            toastMessage({ message: TOAST_ERROR_MESSAGE, type: 'error' });
            console.error('Unexpected error on verify token.');
            goBackToLoginStep({ setLoginStep, setLoginInput });
        }
      });
  };

  const onChangeToken = (value: string) => {
    if (hasError) {
      setHasError(false);
    }
    setVerifyToken(value);
  };
  return (
    <AuthFormContainer>
      <AuthFormHint>
        Enter the 6-digit verification code generated by the authenticator app.
      </AuthFormHint>
      <AuthFieldContainer>
        <AuthFieldLabel>Verification code</AuthFieldLabel>
        <AuthInputField
          placeholder={'- - - - - -'}
          value={verifyToken}
          onChange={onChangeToken}
          autoComplete={'off'}
          type="text"
        />
        {hasError && (
          <FieldError
            type="custom"
            customError={'Token is not correct, please try again.'}
          />
        )}
      </AuthFieldContainer>
      <AuthSubmitButton
        type="submit"
        loading={loading}
        onClick={onVerifyMfa}
        trackContext="login"
        trackAction="mfa verify - confirm"
      >
        Confirm
      </AuthSubmitButton>
    </AuthFormContainer>
  );
};

export default MfaSetupForm;
